/**
 * App versions — which build every dealer runs, and who still needs to update.
 *
 * Data: `users/{uid}.installs.{play|sideload|web}` — each channel is stamped in
 * its own slot by the customer app when it opens (hourly at most, immediately
 * after an update), so a browser visit can never erase a Play record — and
 * `app_config.apk.playVersionCode` (published by scripts/mark-play-live.mjs
 * once Google approves a rollout). Everything here is derived client-side from
 * the users list the panel already streams — no extra reads.
 */
import { useMemo, useState } from 'react';
import type { AppChannel, User } from '@mitang/shared';
import { useAppConfig, useUsers } from '../hooks/useFirestore';
import {
  appVersionKey,
  appVersionState,
  appVersionSummary,
  primaryInstall,
  type AppVersionState,
  type PrimaryInstall,
} from '../lib/analytics';
import { toCsv, downloadCsv } from '../lib/csv';
import { formatDate, formatDateTime, timeAgo } from '../lib/format';
import { Badge, Button, Card, SectionHeader, StatusPill } from '../components/ui';
import { StatCard } from '../components/StatCard';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { AppVersionBreakdown, StateBadge, STATE_META } from '../components/AppVersionBreakdown';

type SortKey = 'email' | 'companyName' | 'city' | 'state' | 'build' | 'appSeenAt' | 'lastActiveAt';
type SortDir = 'asc' | 'desc';

/** Most actionable first when sorting by state. */
const STATE_ORDER: Record<AppVersionState, number> = {
  behind: 0,
  latest: 1,
  sideload: 2,
  web: 3,
  unknown: 4,
};

const CHANNEL_LABEL: Record<AppChannel, string> = {
  play: 'Play Store',
  sideload: 'Sideloaded APK',
  web: 'Browser',
};

/** "v1.4.2 (build 7)" / "build 7" / "—" for the table's version cell. */
function buildText(p: PrimaryInstall | null): string {
  if (!p || p.channel === 'web' || p.build === undefined) return '—';
  return p.version ? `v${p.version} (build ${p.build})` : `build ${p.build}`;
}

export function AppVersionsPage(): JSX.Element {
  const users = useUsers();
  const appConfig = useAppConfig();
  const latestPlayBuild = appConfig.data?.apk?.playVersionCode;

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<AppVersionState | 'all'>('all');
  const [versionKey, setVersionKey] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('state');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerUser, setDrawerUser] = useState<User | null>(null);

  const summary = useMemo(
    () => appVersionSummary(users.data, latestPlayBuild),
    [users.data, latestPlayBuild],
  );
  const stateOf = useMemo(() => {
    const m = new Map<string, AppVersionState>();
    for (const u of users.data) m.set(u.id, appVersionState(u, latestPlayBuild));
    return m;
  }, [users.data, latestPlayBuild]);
  const installOf = useMemo(() => {
    const m = new Map<string, PrimaryInstall | null>();
    for (const u of users.data) m.set(u.id, primaryInstall(u));
    return m;
  }, [users.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.data.filter((u) => {
      if (stateFilter !== 'all' && stateOf.get(u.id) !== stateFilter) return false;
      if (versionKey && appVersionKey(u) !== versionKey) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.companyName.toLowerCase().includes(q) ||
        u.city.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.mobile.toLowerCase().includes(q)
      );
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'state':
          return (
            (STATE_ORDER[stateOf.get(a.id) ?? 'unknown'] -
              STATE_ORDER[stateOf.get(b.id) ?? 'unknown']) *
              dir || (installOf.get(b.id)?.build ?? 0) - (installOf.get(a.id)?.build ?? 0)
          );
        case 'build':
          return ((installOf.get(a.id)?.build ?? -1) - (installOf.get(b.id)?.build ?? -1)) * dir;
        case 'appSeenAt':
          return ((installOf.get(a.id)?.seenAt ?? 0) - (installOf.get(b.id)?.seenAt ?? 0)) * dir;
        case 'lastActiveAt':
          return ((a.lastActiveAt ?? 0) - (b.lastActiveAt ?? 0)) * dir;
        default:
          return String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? '')) * dir;
      }
    });
    return list;
  }, [users.data, search, stateFilter, versionKey, sortKey, sortDir, stateOf, installOf]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(
        key === 'email' || key === 'companyName' || key === 'city' || key === 'state'
          ? 'asc'
          : 'desc',
      );
    }
  };

  const exportCsv = () => {
    const data = rows.map((u) => {
      const p = installOf.get(u.id) ?? null;
      const native = p && p.channel !== 'web' ? p : null;
      return {
        email: u.email,
        name: u.name,
        company: u.companyName,
        mobile: u.mobile,
        city: u.city,
        status: u.status,
        channel: p ? CHANNEL_LABEL[p.channel] : '',
        version: native?.version ?? '',
        build: native?.build ?? '',
        updateState: STATE_META[stateOf.get(u.id) ?? 'unknown'].label,
        appSeenAt: p ? formatDateTime(p.seenAt) : '',
        webSeenAt: u.installs?.web ? formatDateTime(u.installs.web.seenAt) : '',
        lastActiveAt: u.lastActiveAt ? formatDateTime(u.lastActiveAt) : '',
      };
    });
    downloadCsv(
      `app-versions-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(data, [
        { key: 'email', header: 'Email' },
        { key: 'name', header: 'Name' },
        { key: 'company', header: 'Company' },
        { key: 'mobile', header: 'Mobile' },
        { key: 'city', header: 'City' },
        { key: 'status', header: 'Account status' },
        { key: 'channel', header: 'Channel' },
        { key: 'version', header: 'App version' },
        { key: 'build', header: 'Build' },
        { key: 'updateState', header: 'Update state' },
        { key: 'appSeenAt', header: 'App last opened' },
        { key: 'webSeenAt', header: 'Browser last used' },
        { key: 'lastActiveAt', header: 'Last active' },
      ]),
    );
  };

  const recordedCount = users.data.length - summary.unrecorded;
  const otherChannels = summary.sideload + summary.web;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="App versions"
        subtitle="Which build each dealer runs, and who still needs to update. Click a row for their activity."
        actions={
          <>
            {latestPlayBuild !== undefined && (
              <Badge tone="brand">Latest Play build {latestPlayBuild}</Badge>
            )}
            <Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
              Export CSV
            </Button>
          </>
        }
      />

      {users.error ? (
        <ErrorState message={users.error} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Updated via Play Store"
            value={summary.playOnLatest}
            tone="emerald"
            loading={users.loading}
            sublabel={`of ${summary.playTotal} Play ${summary.playTotal === 1 ? 'install' : 'installs'} seen`}
          />
          <StatCard
            label="Needs update"
            value={summary.playBehind}
            tone={summary.playBehind > 0 ? 'amber' : 'neutral'}
            loading={users.loading}
            sublabel="Older Play build · blocked until they update"
          />
          <StatCard
            label="Other channels"
            value={otherChannels}
            loading={users.loading}
            sublabel={`${summary.sideload} sideloaded APK · ${summary.web} browser`}
          />
          <StatCard
            label="Not recorded yet"
            value={summary.unrecorded}
            loading={users.loading}
            sublabel={`of ${users.data.length} accounts · haven't opened the app since tracking began`}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">By build</h2>
            <p className="text-xs text-slate-500">Click a build to list only those dealers</p>
          </div>
          <div className="px-3 py-3">
            {users.loading ? (
              <LoadingState label="Loading…" />
            ) : recordedCount === 0 ? (
              <EmptyState
                title="No app versions recorded yet"
                hint="Each dealer's build is stamped the next time they open the app."
              />
            ) : (
              <AppVersionBreakdown
                rows={summary.rows}
                total={users.data.length}
                selectedKey={versionKey}
                onSelect={setVersionKey}
              />
            )}
          </div>
        </Card>

        <div className="space-y-4 xl:col-span-2">
          <Card className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, company, email, city, mobile…"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 sm:max-w-md"
              />
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value as AppVersionState | 'all')}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="all">All dealers</option>
                <option value="latest">Updated</option>
                <option value="behind">Needs update</option>
                <option value="sideload">Sideloaded APK</option>
                <option value="web">Browser</option>
                <option value="unknown">Not recorded</option>
              </select>
              {versionKey && (
                <Button variant="ghost" size="sm" onClick={() => setVersionKey(null)}>
                  Clear build filter ✕
                </Button>
              )}
              <span className="text-xs text-slate-500 sm:ml-auto">
                Showing {rows.length} of {users.data.length}
              </span>
            </div>
          </Card>

          {users.loading ? (
            <LoadingState label="Loading dealers…" />
          ) : users.data.length === 0 ? (
            <EmptyState title="No users yet" hint="Approved registrations become user accounts." />
          ) : rows.length === 0 ? (
            <EmptyState title="No matches" hint="Try a different search or filter." />
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <SortableTh
                        label="Dealer"
                        active={sortKey === 'email'}
                        dir={sortDir}
                        onClick={() => toggleSort('email')}
                      />
                      <SortableTh
                        label="Company"
                        active={sortKey === 'companyName'}
                        dir={sortDir}
                        onClick={() => toggleSort('companyName')}
                      />
                      <SortableTh
                        label="City"
                        active={sortKey === 'city'}
                        dir={sortDir}
                        onClick={() => toggleSort('city')}
                      />
                      <SortableTh
                        label="Update"
                        active={sortKey === 'state'}
                        dir={sortDir}
                        onClick={() => toggleSort('state')}
                      />
                      <SortableTh
                        label="App version"
                        active={sortKey === 'build'}
                        dir={sortDir}
                        onClick={() => toggleSort('build')}
                      />
                      <SortableTh
                        label="App last opened"
                        active={sortKey === 'appSeenAt'}
                        dir={sortDir}
                        onClick={() => toggleSort('appSeenAt')}
                      />
                      <SortableTh
                        label="Last active"
                        active={sortKey === 'lastActiveAt'}
                        dir={sortDir}
                        onClick={() => toggleSort('lastActiveAt')}
                      />
                      <th className="px-4 py-3 font-medium">Account</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((u) => {
                      const state = stateOf.get(u.id) ?? 'unknown';
                      const p = installOf.get(u.id) ?? null;
                      return (
                        <tr
                          key={u.id}
                          onClick={() => setDrawerUser(u)}
                          className="cursor-pointer transition-colors hover:bg-slate-50"
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{u.email || '—'}</div>
                            <div className="text-xs text-slate-400">
                              {u.name || (u.companyName ? '' : 'No profile yet')}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.companyName || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{u.city || '—'}</td>
                          <td className="px-4 py-3">
                            <StateBadge state={state} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="tabular-nums text-slate-800">{buildText(p)}</div>
                            <div className="text-xs text-slate-400">
                              {p ? CHANNEL_LABEL[p.channel] : 'Not recorded'}
                              {p && p.channel !== 'web' && u.installs?.web
                                ? ` · also browser ${timeAgo(u.installs.web.seenAt)}`
                                : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600" title={formatDate(p?.seenAt)}>
                            {p ? timeAgo(p.seenAt) : '—'}
                          </td>
                          <td
                            className="px-4 py-3 text-slate-600"
                            title={formatDate(u.lastActiveAt)}
                          >
                            {u.lastActiveAt ? timeAgo(u.lastActiveAt) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill status={u.status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>

      <ActivityTimeline
        user={drawerUser}
        open={drawerUser !== null}
        onClose={() => setDrawerUser(null)}
      />
    </div>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}): JSX.Element {
  return (
    <th className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${active ? 'text-slate-800' : ''}`}
      >
        {label}
        {active && <span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
